import React from "react";
import { useParams, useLocation, Link, Navigate } from "react-router-dom";
import { getCityBySlug, CITIES } from "../data/cities";
import PageTopSlider from "../components/PageTopSlider";
import ContactForm from "../components/ContactForm";
import ReviewsSlider from "../components/ReviewsSlider";
import GalleryViewer from "../components/GalleryViewer";
import SEO from "../components/SEO";

export default function CityEnergikonsulent({ cityProp }) {
  const { citySlug } = useParams();
  const location = useLocation();

  const slugFromPath = location.pathname.replace(/^\/energikonsulent-i-/, "");
  const city = cityProp || getCityBySlug(citySlug) || getCityBySlug(slugFromPath);

  if (!city) {
    return <Navigate to="/404" replace />;
  }

  const otherCities = CITIES.filter((c) => c.slug !== city.slug);

  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title={`Energikonsulent i ${city.name} og omegn | Ring i dag`}
        description="Mangler du en erfaren energikonsulent, står vi klar med teknisk ekspertise og løsninger skræddersyet til dine behov. Kontakt os her."
        canonical={`https://www.etdanmark.com/energikonsulent-i-${city.slug}`}
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07613.jpg"
      />
      {/* 1. TOP HERO */}
      <PageTopSlider
        title={`Energikonsulent i ${city.name}`}
        subtitle="Mangler du en erfaren energikonsulent, står vi klar med teknisk ekspertise og løsninger skræddersyet til dine behov."
        badge={`Lokal ekspertise i ${city.name} & omegn`}
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: `Energikonsulent i ${city.name}` },
        ]}
        actions={
          <>
            <a
              href="tel:+4542499595"
              className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
              <span>Ring: 42 49 95 95</span>
            </a>
            <Link
              to="/prisberegner"
              className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-calculator text-emerald-300 text-xs" aria-hidden="true"></i>
              <span>Beregn din pris</span>
            </Link>
          </>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07613.jpg",
          alt: `Energikonsulent i ${city.name}`,
        }}
      />

      {/* 2. FORDELE VED AT VÆLGE EN ENERGIKONSULENT */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-7 space-y-6">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-stone-900 tracking-tight leading-tight">
                Fordele ved at vælge en erfaren energikonsulent i {city.name}
              </h2>

              <p className="text-stone-700 leading-relaxed text-base">
                Investering i energirådgivning er ikke kun en økonomisk fordelagtig beslutning, men også et skridt mod en mere bæredygtig fremtid. Her er en detaljeret gennemgang af væsentlige fordele ved at vælge professionel energirådgivning:
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div className="p-5 rounded-2xl bg-stone-50 border border-stone-200/70 hover:border-emerald-500/40 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-emerald-600/10 text-emerald-700 flex items-center justify-center mb-3">
                    <i className="fa-solid fa-piggy-bank text-lg" aria-hidden="true"></i>
                  </div>
                  <h3 className="font-bold text-stone-900 text-sm mb-1.5">
                    Reducerede energiomkostninger
                  </h3>
                  <p className="text-xs text-stone-600 leading-relaxed">
                    En energikonsulent identificerer de største energiomkostninger i din bolig eller virksomhed, og foreslår effektive løsninger til at reducere dit forbrug.
                  </p>
                </div>

                <div className="p-5 rounded-2xl bg-stone-50 border border-stone-200/70 hover:border-emerald-500/40 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-emerald-600/10 text-emerald-700 flex items-center justify-center mb-3">
                    <i className="fa-solid fa-wind text-lg" aria-hidden="true"></i>
                  </div>
                  <h3 className="font-bold text-stone-900 text-sm mb-1.5">
                    Forbedret indeklima
                  </h3>
                  <p className="text-xs text-stone-600 leading-relaxed">
                    Optimering af opvarmning og ventilation, fører til et sundere indeklima og færre trækgener.
                  </p>
                </div>

                <div className="p-5 rounded-2xl bg-stone-50 border border-stone-200/70 hover:border-emerald-500/40 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-emerald-600/10 text-emerald-700 flex items-center justify-center mb-3">
                    <i className="fa-solid fa-house-circle-check text-lg" aria-hidden="true"></i>
                  </div>
                  <h3 className="font-bold text-stone-900 text-sm mb-1.5">
                    Øget ejendomsværdi
                  </h3>
                  <p className="text-xs text-stone-600 leading-relaxed">
                    Opdaterede energisystemer og bedre isolering kan øge din ejendoms markedspris ved salg eller udlejning.
                  </p>
                </div>

                <div className="p-5 rounded-2xl bg-stone-50 border border-stone-200/70 hover:border-emerald-500/40 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-emerald-600/10 text-emerald-700 flex items-center justify-center mb-3">
                    <i className="fa-solid fa-leaf text-lg" aria-hidden="true"></i>
                  </div>
                  <h3 className="font-bold text-stone-900 text-sm mb-1.5">
                    Miljømæssige fordele
                  </h3>
                  <p className="text-xs text-stone-600 leading-relaxed">
                    Ved at reducere energiforbruget bidrager du positivt til miljøet ved at sænke dit CO2-aftryk.
                  </p>
                </div>
              </div>

              <div className="p-6 rounded-2xl bg-emerald-900/5 border border-emerald-800/15 space-y-4">
                <p className="text-sm text-stone-700 leading-relaxed">
                  Som energikonsulenter i {city.name} arbejder vi tæt sammen med dig for at sikre bæredygtige og omkostningseffektive resultater. Med vores dedikation og ekspertise vil vi guide dig igennem alle faser af energioptimering, og sikre din tilfredshed fra start til slut. Du er altid velkommen til at kontakte os, hvis du ønsker at høre mere om hvordan du opnår ovenstående fordele, eller bestille en tid til optimering af energien i din bolig, virksomhed eller institution.
                </p>

                <div className="p-4 rounded-2xl bg-stone-50 border border-stone-200 text-xs text-stone-600 space-y-2 mt-4">
                  <div className="font-semibold text-stone-900">
                    Relaterede ydelser i {city.name}:
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Link to={`/energimaerkning-i-${city.slug}`} className="text-emerald-700 hover:underline font-medium">
                      • Energimærkning i {city.name}
                    </Link>
                    <Link to={`/trykproevning-i-${city.slug}`} className="text-emerald-700 hover:underline font-medium">
                      • Trykprøvning i {city.name}
                    </Link>
                    <Link to="/termografi" className="text-emerald-700 hover:underline font-medium">
                      • Termografisk undersøgelse
                    </Link>
                    <Link to="/energiramme" className="text-emerald-700 hover:underline font-medium">
                      • Energirammeberegninger
                    </Link>
                    {["aarhus", "horsens", "odense"].includes(city.slug) ? (
                      <Link to={`/fugtmaaling-i-${city.slug}`} className="text-emerald-700 hover:underline font-medium">
                        • Fugtmåling i {city.name}
                      </Link>
                    ) : (
                      <Link to="/fugtmaaling" className="text-emerald-700 hover:underline font-medium">
                        • Fugtmåling med Tramex
                      </Link>
                    )}
                    {["aarhus", "horsens", "vejle", "odense"].includes(city.slug) ? (
                      <Link to={`/lca-beregning-i-${city.slug}`} className="text-emerald-700 hover:underline font-medium">
                        • LCA-beregning i {city.name}
                      </Link>
                    ) : (
                      <Link to="/lca-beregning" className="text-emerald-700 hover:underline font-medium">
                        • LCA-beregning efter BR18
                      </Link>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 pt-2">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm transition-colors shadow-sm"
                  >
                    <span>Kontakt os</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true"></i>
                  </Link>
                  <a
                    href="tel:+4542499595"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white hover:bg-stone-100 border border-stone-300 text-stone-800 font-semibold text-sm transition-colors"
                  >
                    <i className="fa-solid fa-phone text-emerald-600 text-xs" aria-hidden="true"></i>
                    <span>42 49 95 95</span>
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5 space-y-6">
              <div className="rounded-3xl overflow-hidden shadow-lg border border-stone-200/80">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07577.jpg"
                  alt={`Energikonsulent rådgivning i ${city.name}`}
                  className="w-full h-80 object-cover object-center"
                />
              </div>

              <div className="p-6 rounded-3xl bg-stone-900 text-white space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center font-bold text-lg">
                    L
                  </div>
                  <div>
                    <div className="font-bold text-base">Lars Lauritzen</div>
                    <div className="text-xs text-stone-300">Certificeret Energikonsulent</div>
                  </div>
                </div>
                <p className="text-xs text-stone-300 leading-relaxed">
                  20 års erfaring i byggebranchen med totalentrepriser og energirådgivning. Vi rykker hurtigt ud til opgaver i {city.name} og omegn.
                </p>
                <div className="pt-2 border-t border-stone-800 flex items-center justify-between text-xs text-stone-400">
                  <span>Direkte kontakt:</span>
                  <a href="tel:+4542499595" className="text-emerald-400 font-bold hover:underline">
                    42 49 95 95
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. SE DET SKJULTE MED TERMOGRAFI */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden shadow-md border border-stone-200/80">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07476-2-0.jpg"
                  alt={`Termografi undersøgelse i ${city.name}`}
                  className="w-full h-80 object-cover object-center"
                />
              </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2 space-y-5">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                Se det skjulte med termografi
              </h2>

              <p className="text-stone-700 text-sm leading-relaxed">
                Med termografi får du et tydeligt billede af, hvor varmen forsvinder fra din bolig eller virksomhed. Vores varmekamera afslører kuldebroer, utætheder og dårlig isolering – problemer, der ellers er usynlige for øjet.
              </p>

              <div className="space-y-2.5 pt-1">
                <div className="flex items-center gap-3 text-sm text-stone-800 font-medium">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs flex-shrink-0">
                    <i className="fa-solid fa-check" aria-hidden="true"></i>
                  </div>
                  <span>Klar dokumentation på bygningens tilstand</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-stone-800 font-medium">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs flex-shrink-0">
                    <i className="fa-solid fa-check" aria-hidden="true"></i>
                  </div>
                  <span>Mulighed for at spare på varmeregningen</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-stone-800 font-medium">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs flex-shrink-0">
                    <i className="fa-solid fa-check" aria-hidden="true"></i>
                  </div>
                  <span>Bedre komfort og indeklima</span>
                </div>
              </div>

              <p className="text-stone-700 text-sm leading-relaxed pt-2">
                Kontakt os i dag og få en professionel termografi af din bygning i {city.name}– så du ved præcis, hvor du skal sætte ind.
              </p>

              <div className="pt-3 flex flex-wrap gap-3">
                <Link
                  to="/termografi"
                  className="px-5 py-2.5 rounded-full bg-stone-900 hover:bg-stone-800 text-white font-semibold text-xs transition-colors"
                >
                  Læs mere om termografi
                </Link>
                <Link
                  to="/kontakt"
                  className="px-5 py-2.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-xs transition-colors"
                >
                  Få et tilbud
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. FÅ HJÆLP FRA EN ERFAREN ENERGIKONSULENT I [BY] I DAG */}
      <section className="py-14 sm:py-18 bg-emerald-950 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-5">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
            Få hjælp fra en erfaren energikonsulent i {city.name} i dag
          </h2>
          <p className="text-emerald-200 text-sm max-w-2xl mx-auto leading-relaxed">
            Er du klar til at tage det næste skridt mod en mere energieffektiv fremtid i {city.name}? Så er en energikonsulent det rette valg. Kontakt os i dag for en uforpligtende snak om dine muligheder.
          </p>
          <div className="pt-2 flex flex-wrap justify-center gap-4 text-sm font-semibold">
            <a
              href="tel:+4542499595"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold transition-all shadow-md"
            >
              <i className="fa-solid fa-phone" aria-hidden="true"></i>
              <span>Ring: 42 49 95 95</span>
            </a>
            <a
              href="mailto:info@etdanmark.com"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-900/80 hover:bg-emerald-800 border border-emerald-700 text-white transition-all"
            >
              <i className="fa-solid fa-envelope text-emerald-300" aria-hidden="true"></i>
              <span>info@etdanmark.com</span>
            </a>
          </div>
        </div>
      </section>

      {/* 5. REFERENCER / GALLERI */}
      <section className="py-16 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <GalleryViewer
            gallerySlug="referencer"
            title={`Billeder fra marken i ${city.name} og omegn`}
            subtitle="Eksempler på udførte målinger, gennemgange og energioptimering"
            maxImages={8}
          />
        </div>
      </section>

      {/* 6. ANDRE BYER I JYLLAND */}
      <section className="py-12 bg-stone-100 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h3 className="text-lg font-bold text-stone-900 mb-2">
            Vi arbejder også som energikonsulenter i andre byer i Jylland og Fyn:
          </h3>
          <p className="text-xs text-stone-600 mb-6">
            Klik på en by for at læse mere om vores rådgivning i netop dit lokalområde
          </p>
          <div className="flex flex-wrap justify-center gap-2 max-w-4xl mx-auto">
            {otherCities.map((c) => (
              <Link
                key={c.slug}
                to={`/energikonsulent-i-${c.slug}`}
                className="px-3.5 py-1.5 rounded-xl bg-white border border-stone-300/80 text-xs font-semibold text-stone-700 hover:bg-emerald-50 hover:border-emerald-500 hover:text-emerald-800 transition-all shadow-2xs"
              >
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 7. REVIEWS */}
      <ReviewsSlider />

      {/* 8. KONTAKTFORMULAR */}
      <section className="py-16 sm:py-20 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Bestil energikonsulent i {city.name}
            </h2>
            <p className="text-sm text-stone-600 mt-2 max-w-lg mx-auto">
              Du kan let og hurtigt sende os en besked i nedenstående formular, så vender vi tilbage hurtigst muligt.
            </p>
          </div>

          <div className="bg-white p-6 sm:p-10 rounded-3xl border border-stone-200 shadow-sm">
            <ContactForm />
          </div>
        </div>
      </section>
    </div>
  );
}
